import React, { useState } from 'react';
import { EmailBlock, SocialBlockContent } from '../../types';
import { Plus, X } from 'lucide-react';

interface SocialPropertiesProps {
  block: EmailBlock;
  onUpdate: (updates: Partial<SocialBlockContent>) => void;
  mergeTags?: Array<{ name: string; value: string; sample: string }>;
  isDarkMode: boolean;
}

const SOCIAL_PLATFORMS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' }
] as const;

export const SocialProperties: React.FC<SocialPropertiesProps> = ({
  block,
  onUpdate,
  isDarkMode
}) => {
  const content = block.content as SocialBlockContent;
  const [newPlatform, setNewPlatform] = useState<string>('facebook');
  const [newUrl, setNewUrl] = useState<string>('');

  const handleAddPlatform = () => {
    if (!newUrl.trim()) return;
    const platforms = [...(content.platforms || []), {
      platform: newPlatform as any,
      url: newUrl
    }];
    onUpdate({ platforms });
    setNewUrl('');
  };

  const handleRemovePlatform = (index: number) => {
    const platforms = content.platforms?.filter((_, i) => i !== index) || [];
    onUpdate({ platforms });
  };

  const handleUpdateUrl = (index: number, url: string) => {
    const platforms = [...(content.platforms || [])];
    platforms[index] = { ...platforms[index], url };
    onUpdate({ platforms });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={`
          block text-sm font-medium mb-2
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Social Platforms
        </label>
        
        {content.platforms && content.platforms.length > 0 && (
          <div className="space-y-2 mb-3">
            {content.platforms.map((platform, index) => (
              <div key={index} className={`
                flex items-center gap-2 p-2 rounded
                ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}
              `}>
                <span className={`
                  text-sm flex-1
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                `}>
                  {SOCIAL_PLATFORMS.find(p => p.value === platform.platform)?.label}
                </span>
                <input
                  type="url"
                  value={platform.url}
                  onChange={(e) => handleUpdateUrl(index, e.target.value)}
                  className={`
                    flex-1 px-2 py-1 text-sm border rounded
                    ${isDarkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  placeholder="https://..."
                />
                <button
                  onClick={() => handleRemovePlatform(index)}
                  className={`
                    p-1 rounded hover:bg-opacity-80
                    ${isDarkMode ? 'text-error-400 hover:bg-gray-600' : 'text-error-600 hover:bg-gray-200'}
                  `}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <select
            value={newPlatform}
            onChange={(e) => setNewPlatform(e.target.value)}
            className={`
              px-3 py-2 border rounded-md text-sm
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          >
            {SOCIAL_PLATFORMS.map(platform => (
              <option key={platform.value} value={platform.value}>
                {platform.label}
              </option>
            ))}
          </select>
          <input
            type="url"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddPlatform()}
            className={`
              flex-1 px-3 py-2 border rounded-md text-sm
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
            placeholder="URL"
          />
          <button
            onClick={handleAddPlatform}
            className={`
              px-3 py-2 rounded-md
              ${isDarkMode 
                ? 'bg-accent-600 hover:bg-accent-700 text-white' 
                : 'bg-accent-500 hover:bg-accent-600 text-white'
              }
            `}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Icon Size
          </label>
          <input
            type="number"
            value={content.iconSize || 32}
            onChange={(e) => onUpdate({ iconSize: parseInt(e.target.value) || 32 })}
            className={`
              w-full px-3 py-2 border rounded-md
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
            min="16"
            max="64"
          />
        </div>

        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Spacing
          </label>
          <input
            type="number"
            value={content.spacing || 10}
            onChange={(e) => onUpdate({ spacing: parseInt(e.target.value) || 10 })}
            className={`
              w-full px-3 py-2 border rounded-md
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
            min="0"
            max="50"
          />
        </div>
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Icon Color
        </label>
        <input
          type="color"
          value={content.iconColor || '#000000'}
          onChange={(e) => onUpdate({ iconColor: e.target.value })}
          className="w-full h-10 border rounded-md"
        />
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Alignment
        </label>
        <select
          value={content.align || 'center'}
          onChange={(e) => onUpdate({ align: e.target.value as any })}
          className={`
            w-full px-3 py-2 border rounded-md
            ${isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>
    </div>
  );
};
